import { Component, OnInit, ViewEncapsulation } from "@angular/core";
import { DomSanitizer, SafeResourceUrl } from "@angular/platform-browser";
import { BroadcastService } from "../broadcast/broadcast-service.service";
import { firstValueFrom } from "rxjs";
import { TranslateService } from "@ngx-translate/core";

declare var customToastr: any;

interface AudienceOption {
  key: number;
  label: string;
  peopleCount: number;
  icon: string;
  receiverType: string;
}

interface BroadcastMedia {
  type: "image" | "document";
  label: string;
  url: string;
}

interface BroadcastViewer {
  userId: number;
  name: string;
  initials: string;
  image: string | null;
  avatarColor: string;
  className: string | null;
  type: string;
  viewedAt: string | null;
  viewedDate: string | null;
}

interface BroadcastRow {
  broadcastId?: number;
  title: string;
  subtitle: string;
  sentOnDate: string;
  sentOnTime: string;
  views: number | null;
  readPercent: number | null;
  status: "Sent";
  message?: string;
  recipients?: number;
  viewers?: BroadcastViewer[];
  media?: BroadcastMedia[];
  isHighPriority?: boolean;
  creatorName?: string;
  creatorImage?: string;
  creatorRole?: string;
}

@Component({
  selector: "app-broadcast-v2",
  templateUrl: "./broadcast-v2.component.html",
  styleUrls: [
    "./../../assets/css/theme-default/bootstrap.css",
    "./../../assets/css/theme-default/materialadmin.css",
    "./../../assets/css/theme-default/font-awesome.min.css",
    "./../../assets/css/theme-default/material-design-iconic-font.min.css",
    "./broadcast-v2.component.css",
  ],
  encapsulation: ViewEncapsulation.None,
  providers: [BroadcastService],
})
export class BroadcastV2Component implements OnInit {
  constructor(
    private broadcastService: BroadcastService,
    private sanitizer: DomSanitizer,
    private translate: TranslateService
  ) {}

  broadcastStats: { slug: string; title: string; value: number }[] = [];

  isCreateBroadcastEnabled = false;

  academicYears: { id: number; label: string; isCurrent: boolean }[] = [];
  selectedAcademicYearId: number | null = null;

  searchTerm = "";
  private lastSearchedTerm = "";

  selectedStatusLabel: string | null = null;
  statusFilterOptions: { label: string; receiverType: string }[] = [];

  isChooseDateOpen = false;
  selectedDateFilterKey = "All time";
  selectedDate: Date | null = null;
  customDateLabel = "";

  broadcasts: BroadcastRow[] = [];
  isLoadingBroadcasts = false;
  readonly skeletonRows = Array.from({ length: 8 }, () => ({}));
  readonly skeletonStatCards = Array.from({ length: 4 }, () => ({}));

  currentPage = 1;
  pageSize = 10;
  totalPages = 1;
  totalRecords = 0;

  get paginatorFirst(): number {
    return (this.currentPage - 1) * this.pageSize;
  }

  isDetailPanelOpen = false;
  selectedBroadcast: BroadcastRow | null = null;

  isLoadingViewers = false;
  viewersCurrentPage = 1;
  hasMoreViewers = true;
  viewersSearchTerm = "";
  private lastViewersSearchedTerm = "";
  private viewersSearchDebounceTimer: any = null;
  readonly viewersPageLimit = 20;
  // Resolved lazily (not at class field init) since the CSS custom
  // properties aren't guaranteed to be attached to :root yet at that point.
  private get avatarColorPalette(): string[] {
    const rootStyle = getComputedStyle(document.documentElement);
    return [
      rootStyle.getPropertyValue('--brand-primary-color').trim() || '#158ED2',
      rootStyle.getPropertyValue('--primary-green-color').trim() || '#37B904',
      rootStyle.getPropertyValue('--menubar-accent-gradient-color').trim() || '#B458DF',
      rootStyle.getPropertyValue('--warning-color').trim() || '#E2941F',
    ];
  }

  isMediaPreviewOpen = false;
  previewMedia: BroadcastMedia | null = null;
  previewMediaSafeUrl: SafeResourceUrl | null = null;

  isNewBroadcastPanelOpen = false;
  audienceOptions: AudienceOption[] = [];
  selectedAudience: number | null = null;
  newBroadcastTitle = "";
  newBroadcastDescription = "";
  newBroadcastAttachments: File[] = [];
  isNewBroadcastDragOver = false;
  isSendingBroadcast = false;
  readonly maxBroadcastAttachments = 5;
  readonly allowedBroadcastAttachmentTypes = ["image/jpeg", "image/png", "image/jpg", "image/gif", "image/webp", "application/pdf"];

  private getAudienceIcon(receiver: string): string {
    switch ((receiver || "").toUpperCase()) {
      case "STAFFS":
      case "STAFF":
        return "ph-user";
      case "PARENTS":
        return "ph-users";
      case "ALL":
      default:
        return "ph-globe";
    }
  }

  private toTitleCase(value: string): string {
    if (!value) {
      return "";
    }
    return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
  }

  getStatIcon(slug: string): string {
    switch (slug) {
      case "total-broadcast":
        return "ph-paper-plane-tilt";
      case "all":
        return "ph-globe";
      case "parents":
        return "ph-users";
      case "staffs":
        return "ph-user";
      default:
        return "ph-chart-bar";
    }
  }

  getStatIconClass(slug: string): string {
    switch (slug) {
      case "total-broadcast":
        return "bv2-icon-blue";
      case "all":
        return "bv2-icon-purple";
      case "parents":
        return "bv2-icon-green";
      case "staffs":
        return "bv2-icon-orange";
      default:
        return "bv2-icon-blue";
    }
  }

  async getAcademicYears(): Promise<void> {
    try {
      const response: any = await firstValueFrom(this.broadcastService.getAcademicYears());
      if (response?.status && response?.body?.currentAndPastAcademicData) {
        this.academicYears = response.body.currentAndPastAcademicData.map((year: any) => ({
          id: year.pkAcademicId,
          label: year.Year,
          isCurrent: !!year.isCurrentAcademic,
        }));
        const currentYear = this.academicYears.find((year) => year.isCurrent);
        const previousAcademicYearId = this.selectedAcademicYearId;
        this.selectedAcademicYearId = currentYear ? currentYear.id : (this.academicYears[0]?.id ?? null);
        if (this.selectedAcademicYearId && this.selectedAcademicYearId !== previousAcademicYearId) {
          this.getBroadcastReceiverList();
          this.getBroadcastMessages();
        }
      } else {
        const bv2AcademicYearsFetchErrorTranslation = await firstValueFrom(this.translate.get('bv2AcademicYearsFetchError'));
        customToastr(bv2AcademicYearsFetchErrorTranslation, "error");
      }
    } catch (error) {
      const bv2AcademicYearsFetchExceptionTranslation = await firstValueFrom(this.translate.get('bv2AcademicYearsFetchException'));
      customToastr(bv2AcademicYearsFetchExceptionTranslation, "error");
    }
  }

  async getBroadcastReceiverList(): Promise<void> {
    if (!this.selectedAcademicYearId) {
      return;
    }
    try {
      const response: any = await firstValueFrom(
        this.broadcastService.getBroadcastReceiverListV2(this.selectedAcademicYearId)
      );
      if (response?.status && response?.body?.broadcastReceiverData) {
        this.audienceOptions = response.body.broadcastReceiverData.map((item: any) => ({
          key: item.receiverId,
          label: this.toTitleCase(item.receiver),
          peopleCount: item.count,
          icon: this.getAudienceIcon(item.receiver),
          receiverType: item.receiver,
        }));
        await this.buildStatusFilterOptions();
        const allOption = this.audienceOptions.find((option) => option.label.toUpperCase() === "ALL");
        this.selectedAudience = allOption ? allOption.key : (this.audienceOptions[0]?.key ?? null);
      } else {
        const bv2RecipientsFetchErrorTranslation = await firstValueFrom(this.translate.get('bv2RecipientsFetchError'));
        customToastr(bv2RecipientsFetchErrorTranslation, "error");
      }
    } catch (error) {
      const bv2RecipientsFetchExceptionTranslation = await firstValueFrom(this.translate.get('bv2RecipientsFetchException'));
      customToastr(bv2RecipientsFetchExceptionTranslation, "error");
    }
  }

  private async buildStatusFilterOptions(): Promise<void> {
    const bv2SentToPrefixTranslation = await firstValueFrom(this.translate.get('bv2SentToPrefix'));
    this.statusFilterOptions = this.audienceOptions.map((option) => ({
      label: `${bv2SentToPrefixTranslation}${option.label}`,
      receiverType: option.receiverType,
    }));
  }

  onAcademicYearChange(): void {
    this.selectedAcademicYearId = this.selectedAcademicYearId ? +this.selectedAcademicYearId : null;
    this.getBroadcastReceiverList();
    this.currentPage = 1;
    this.getBroadcastMessages();
  }

  private formatDateYYYYMMDD(date: Date): string {
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, "0");
    const day = `${date.getDate()}`.padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  private getDateRangeForFilter(): { startDate: string | null; endDate: string | null } {
    const today = new Date();
    if (this.selectedDateFilterKey === "Last 30 days") {
      const start = new Date(today);
      start.setDate(start.getDate() - 30);
      return { startDate: this.formatDateYYYYMMDD(start), endDate: this.formatDateYYYYMMDD(today) };
    }
    if (this.selectedDateFilterKey === "Last 7 days") {
      const start = new Date(today);
      start.setDate(start.getDate() - 7);
      return { startDate: this.formatDateYYYYMMDD(start), endDate: this.formatDateYYYYMMDD(today) };
    }
    if (this.selectedDate) {
      const formatted = this.formatDateYYYYMMDD(this.selectedDate);
      return { startDate: formatted, endDate: formatted };
    }
    return { startDate: null, endDate: null };
  }

  private getReceiverTypeForFilter(): string | null {
    const match = this.statusFilterOptions.find((option) => option.label === this.selectedStatusLabel);
    return match ? match.receiverType : null;
  }

  async getBroadcastMessages(): Promise<void> {
    if (!this.selectedAcademicYearId) {
      return;
    }
    this.isLoadingBroadcasts = true;
    try {
      const { startDate, endDate } = this.getDateRangeForFilter();
      const response: any = await firstValueFrom(
        this.broadcastService.getBroadcastMessagesV2(
          this.selectedAcademicYearId,
          this.getReceiverTypeForFilter(),
          this.searchTerm?.trim() || null,
          startDate,
          endDate,
          this.currentPage,
          this.pageSize
        )
      );
      if (response?.status && response?.body) {
        this.broadcasts = (response.body.broadcastDetail || []).map((item: any) => this.mapBroadcastDetail(item));
        this.broadcastStats = response.body.stats || [];
        this.totalPages = response.body.maxPageNumber || 1;
        const totalFromStats = this.broadcastStats.find((s) => s.slug === "total-broadcast")?.value ?? this.broadcasts.length;
        this.totalRecords = totalFromStats;
      } else {
        const bv2BroadcastsFetchErrorTranslation = await firstValueFrom(this.translate.get('bv2BroadcastsFetchError'));
        customToastr(bv2BroadcastsFetchErrorTranslation, "error");
      }
    } catch (error) {
      const bv2BroadcastsFetchExceptionTranslation = await firstValueFrom(this.translate.get('bv2BroadcastsFetchException'));
      customToastr(bv2BroadcastsFetchExceptionTranslation, "error");
    } finally {
      this.isLoadingBroadcasts = false;
    }
  }

  private mapBroadcastDetail(item: any): BroadcastRow {
    const media: BroadcastMedia[] = (item.broadcastMediaList || []).map((m: any, index: number) => ({
      type: m.broadcastMediaType === "IMAGE" ? "image" : "document",
      label: `Attachment ${index + 1}`,
      url: m.broadcastMediaUrl,
    }));
    return {
      broadcastId: item.broadcastId,
      title: item.broadcastSubject,
      subtitle: `Sent to ${this.toTitleCase(item.broadcastReceiver)}`,
      sentOnDate: item.sentOnDate,
      sentOnTime: item.sentOnTime,
      views: item.views ?? null,
      readPercent: item.readPercentage ?? null,
      status: "Sent",
      message: item.broadcastMessage,
      // NOTE: individual viewer/who-viewed-when data is not yet returned by
      // the backend for this endpoint — kept as a dummy placeholder until available.
      recipients: item.expectedViews ?? 0,
      viewers: [],
      media,
      isHighPriority: !!item.isHighPriority,
      creatorName: item.broadcastCreatorName,
      creatorImage: item.broadcastCreatorImage,
      creatorRole: item.createdPersonRole,
    };
  }

  ngOnInit(): void {
    const adminPermissionlist = localStorage.getItem("adminPermissionlist");
    if (adminPermissionlist) {
      const permissions = JSON.parse(adminPermissionlist);
      this.isCreateBroadcastEnabled = permissions.CREATE_BROADCAST === "ENABLED";
    }

    const schoolAllInfo = localStorage.getItem("schoolAllInfo");
    if (schoolAllInfo) {
      const schoolInfo = JSON.parse(schoolAllInfo);
      if (schoolInfo?.currentAcademicId) {
        this.selectedAcademicYearId = schoolInfo.currentAcademicId;
        this.isLoadingBroadcasts = true;
        this.getBroadcastReceiverList();
        this.getBroadcastMessages();
      }
    }

    this.getAcademicYears();
  }

  private searchDebounceTimer: any = null;

  onSearchChange(): void {
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
    }
    this.searchDebounceTimer = setTimeout(() => {
      this.searchTerm = this.searchTerm?.trim() || "";
      if (this.searchTerm === this.lastSearchedTerm) {
        return;
      }
      this.lastSearchedTerm = this.searchTerm;
      this.currentPage = 1;
      this.getBroadcastMessages();
    }, 400);
  }

  openNewBroadcastPanel(): void {
    this.isNewBroadcastPanelOpen = true;
  }

  closeNewBroadcastPanel(): void {
    this.isNewBroadcastPanelOpen = false;
    const allOption = this.audienceOptions.find((option) => option.label.toUpperCase() === "ALL");
    this.selectedAudience = allOption ? allOption.key : (this.audienceOptions[0]?.key ?? null);
    this.newBroadcastTitle = "";
    this.newBroadcastDescription = "";
    this.newBroadcastAttachments = [];
    this.isNewBroadcastDragOver = false;
  }

  selectAudience(key: number): void {
    this.selectedAudience = key;
  }

  private async addNewBroadcastFiles(files: FileList | null): Promise<void> {
    if (!files || files.length === 0) {
      return;
    }
    const incoming = Array.from(files);
    const invalidType = incoming.find((file) => !this.allowedBroadcastAttachmentTypes.includes(file.type));
    if (invalidType) {
      const bv2AttachmentTypeErrorTranslation = await firstValueFrom(this.translate.get('bv2AttachmentTypeError'));
      customToastr(bv2AttachmentTypeErrorTranslation, "error");
      return;
    }
    const availableSlots = this.maxBroadcastAttachments - this.newBroadcastAttachments.length;
    if (availableSlots <= 0) {
      const bv2AttachmentLimitMessageTranslation = await firstValueFrom(
        this.translate.get('bv2AttachmentLimitMessage', { count: this.maxBroadcastAttachments })
      );
      customToastr(bv2AttachmentLimitMessageTranslation, "error");
      return;
    }
    if (incoming.length > availableSlots) {
      const bv2AttachmentLimitMessageTranslation = await firstValueFrom(
        this.translate.get('bv2AttachmentLimitMessage', { count: this.maxBroadcastAttachments })
      );
      customToastr(bv2AttachmentLimitMessageTranslation, "error");
    }
    this.newBroadcastAttachments = [...this.newBroadcastAttachments, ...incoming.slice(0, availableSlots)];
  }

  async onNewBroadcastFileChange(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    await this.addNewBroadcastFiles(input.files);
    input.value = "";
  }

  onNewBroadcastDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isNewBroadcastDragOver = true;
  }

  onNewBroadcastDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isNewBroadcastDragOver = false;
  }

  async onNewBroadcastDrop(event: DragEvent): Promise<void> {
    event.preventDefault();
    event.stopPropagation();
    this.isNewBroadcastDragOver = false;
    await this.addNewBroadcastFiles(event.dataTransfer?.files ?? null);
  }

  removeNewBroadcastAttachment(index: number): void {
    this.newBroadcastAttachments = this.newBroadcastAttachments.filter((_, i) => i !== index);
  }

  async sendNewBroadcast(): Promise<void> {
    if (!this.newBroadcastTitle.trim() || !this.newBroadcastDescription.trim() || !this.selectedAudience) {
      return;
    }
    if (!this.selectedAcademicYearId) {
      const bv2SelectAcademicYearErrorTranslation = await firstValueFrom(this.translate.get('bv2SelectAcademicYearError'));
      customToastr(bv2SelectAcademicYearErrorTranslation, "error");
      return;
    }
    this.isSendingBroadcast = true;
    try {
      const body = {
        lang: "en",
        academicId: this.selectedAcademicYearId.toString(),
        broadcastReceiverId: this.selectedAudience.toString(),
        isHighPriority: "false",
        broadcastMessage: this.newBroadcastDescription,
        broadcastSubject: this.newBroadcastTitle,
      };
      const response: any = await firstValueFrom(
        this.broadcastService.createBroadcastServiceV2(body, this.newBroadcastAttachments)
      );
      if (response?.status) {
        const bv2SendSuccessMessageTranslation = await firstValueFrom(this.translate.get('bv2SendSuccessMessage'));
        customToastr(bv2SendSuccessMessageTranslation, "success");
        this.closeNewBroadcastPanel();
        this.currentPage = 1;
        this.getBroadcastMessages();
      } else {
        const bv2SendFailedMessageTranslation = await firstValueFrom(this.translate.get('bv2SendFailedMessage'));
        customToastr(bv2SendFailedMessageTranslation, "error");
      }
    } catch (error) {
      const bv2SendExceptionTranslation = await firstValueFrom(this.translate.get('bv2SendException'));
      customToastr(bv2SendExceptionTranslation, "error");
    } finally {
      this.isSendingBroadcast = false;
    }
  }

  isDetailTitleExpanded = false;

  openBroadcastDetail(broadcast: BroadcastRow): void {
    this.selectedBroadcast = broadcast;
    this.isDetailPanelOpen = true;
    this.isDetailTitleExpanded = false;
    this.viewersCurrentPage = 1;
    this.hasMoreViewers = true;
    this.viewersSearchTerm = "";
    this.lastViewersSearchedTerm = "";
    this.selectedBroadcast.viewers = [];
    this.loadBroadcastViewers();
  }

  closeBroadcastDetail(): void {
    this.isDetailPanelOpen = false;
    this.selectedBroadcast = null;
    this.isDetailTitleExpanded = false;
    this.viewersCurrentPage = 1;
    this.hasMoreViewers = true;
    this.viewersSearchTerm = "";
    this.lastViewersSearchedTerm = "";
  }

  onViewersSearchChange(): void {
    if (this.viewersSearchDebounceTimer) {
      clearTimeout(this.viewersSearchDebounceTimer);
    }
    this.viewersSearchDebounceTimer = setTimeout(() => {
      this.viewersSearchTerm = this.viewersSearchTerm?.trim() || "";
      if (this.viewersSearchTerm === this.lastViewersSearchedTerm) {
        return;
      }
      this.lastViewersSearchedTerm = this.viewersSearchTerm;
      this.viewersCurrentPage = 1;
      this.hasMoreViewers = true;
      if (this.selectedBroadcast) {
        this.selectedBroadcast.viewers = [];
      }
      this.loadBroadcastViewers();
    }, 400);
  }

  private getInitials(name: string): string {
    if (!name) {
      return "?";
    }
    const parts = name.trim().split(/\s+/);
    const first = parts[0]?.charAt(0) || "";
    const last = parts.length > 1 ? parts[parts.length - 1].charAt(0) : "";
    return (first + last).toUpperCase();
  }

  private getAvatarColor(userId: number): string {
    const index = Math.abs(userId) % this.avatarColorPalette.length;
    return this.avatarColorPalette[index];
  }

  async loadBroadcastViewers(): Promise<void> {
    if (!this.selectedBroadcast?.broadcastId || this.isLoadingViewers || !this.hasMoreViewers) {
      return;
    }
    this.isLoadingViewers = true;
    try {
      const response: any = await firstValueFrom(
        this.broadcastService.getBroadcastReceiverDetailList(
          this.selectedBroadcast.broadcastId,
          this.viewersCurrentPage,
          this.viewersPageLimit,
          this.viewersSearchTerm?.trim() || null
        )
      );
      if (response?.status && response?.body) {
        const mapped: BroadcastViewer[] = (response.body.receiverDetailList || []).map((item: any) => ({
          userId: item.userId,
          name: item.name,
          initials: this.getInitials(item.name),
          image: item.image || null,
          avatarColor: this.getAvatarColor(item.userId),
          className: item.className,
          type: item.type,
          viewedAt: item.readTime || null,
          viewedDate: item.readDate || null,
        }));
        const maxPageNumber = response.body.maxPageNumber || 1;
        this.hasMoreViewers = this.viewersCurrentPage < maxPageNumber;
        this.viewersCurrentPage++;
        if (this.selectedBroadcast) {
          this.selectedBroadcast.viewers = [...(this.selectedBroadcast.viewers || []), ...mapped];
          this.selectedBroadcast.recipients = response.body.expectedViews ?? this.selectedBroadcast.recipients;
          if (!this.viewersSearchTerm?.trim() && !this.hasMoreViewers) {
            this.syncBroadcastViewCount(this.selectedBroadcast);
          }
        }
      }
    } catch (error) {
      const bv2ViewersFetchExceptionTranslation = await firstValueFrom(this.translate.get('bv2ViewersFetchException'));
      customToastr(bv2ViewersFetchExceptionTranslation, "error");
    } finally {
      this.isLoadingViewers = false;
    }
  }

  private syncBroadcastViewCount(broadcast: BroadcastRow): void {
    const viewedCount = (broadcast.viewers || []).filter((viewer) => !!viewer.viewedAt).length;
    broadcast.views = viewedCount;
    const row = this.broadcasts.find((b) => b.broadcastId === broadcast.broadcastId);
    if (row && row !== broadcast) {
      row.views = broadcast.views;
    }
  }

  onViewersScroll(event: Event): void {
    const el = event.target as HTMLElement;
    const reachedBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 40;
    if (reachedBottom) {
      this.loadBroadcastViewers();
    }
  }

  toggleDetailTitleExpanded(): void {
    this.isDetailTitleExpanded = !this.isDetailTitleExpanded;
  }

  openMediaPreview(media: BroadcastMedia): void {
    this.previewMedia = media;
    this.previewMediaSafeUrl = media.type === "document"
      ? this.sanitizer.bypassSecurityTrustResourceUrl(media.url)
      : null;
    this.isMediaPreviewOpen = true;
  }

  closeMediaPreview(): void {
    this.isMediaPreviewOpen = false;
    this.previewMedia = null;
    this.previewMediaSafeUrl = null;
  }

  onPaginatorPageChange(event: any): void {
    this.currentPage = event.page + 1;
    this.pageSize = event.rows;
    this.getBroadcastMessages();
  }

  selectStatus(status: string): void {
    this.selectedStatusLabel = status;
    this.currentPage = 1;
    this.getBroadcastMessages();
  }

  onDateFilterChange(event: Event): void {
    const value = (event.target as HTMLSelectElement).value;
    this.selectedDateFilterKey = value;
    if (value === "Choose date") {
      this.isChooseDateOpen = true;
      return;
    }
    this.isChooseDateOpen = false;
    if (value === "All time") {
      this.selectedDate = null;
      this.customDateLabel = "";
    }
    this.currentPage = 1;
    this.getBroadcastMessages();
  }

  onCalendarDateSelected(date: Date): void {
    if (!date) {
      return;
    }
    const formatter = new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "2-digit",
      year: "numeric",
    });
    this.customDateLabel = formatter.format(date);
    this.selectedDateFilterKey = this.customDateLabel;
    this.isChooseDateOpen = false;
    this.currentPage = 1;
    this.getBroadcastMessages();
  }
}
